import React from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '../../components/ui/popover';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { ChatCircleText } from '../../design-system/icons';
import { AuthorAvatar } from './campaignDetailsParts';

interface FieldCommentDotProps {
  /** Only rendered once the field is dirty — matches Figma's "appears the
   *  instant you edit this field" behavior. */
  active: boolean;
  label: string;
  comment: string;
  onCommentChange: (value: string) => void;
  /** Whoever is making the edit — their initial becomes the marker's avatar
   *  once a note exists. */
  authorName: string;
  /** Dot-path this marker belongs to. Becomes the `field-<path>` anchor the
   *  Comments sheet's "Go to field" scrolls to — the marker sits directly
   *  above its field, and it's the one element every commentable field has. */
  path?: string;
}

/**
 * Comment marker for a dirty field — the Figma speech-bubble pin: a gradient
 * sphere in a bubble whose squared bottom-left corner forms the tail. It shows
 * with the "Add a note?" popover already open, so the note is prompted for
 * rather than hunted down, and afterwards reopens that note for editing.
 *
 * Skipping only dismisses the popover — the marker stays for as long as the
 * field is dirty, so a note can still be added later. What Skip does buy you is
 * silence: the Comments sheet lists a change only once its note has text, so a
 * skipped change never shows up there.
 *
 * Writes into the same comments record the Comments sheet reads from, so
 * either surface stays in sync.
 */
export function FieldCommentDot({
  active,
  label,
  comment,
  onCommentChange,
  authorName,
  path,
}: FieldCommentDotProps) {
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState(comment);
  const [wasActive, setWasActive] = React.useState(active);
  // True while the open popover was summoned by an edit rather than a click.
  const [autoOpened, setAutoOpened] = React.useState(false);
  const boxRef = React.useRef<HTMLTextAreaElement>(null);

  // Auto-prompt on the clean→dirty edge only (a render-phase adjustment, the
  // React-sanctioned alternative to an effect — react-hooks/set-state-in-effect
  // forbids sync setState in effect bodies). Edge-triggered, so Skip sticks:
  // typing on in the same field won't re-summon a dismissed popover.
  if (active !== wasActive) {
    setWasActive(active);
    if (active) setDraft(comment);
    setAutoOpened(active);
    setOpen(active);
  }

  // Re-seed the draft on manual open. `autoOpened` is deliberately only
  // cleared here, never on close — Radix fires onCloseAutoFocus after
  // onOpenChange, and it still needs to know the popover was auto-summoned.
  const handleOpenChange = (next: boolean) => {
    if (next) {
      setDraft(comment);
      setAutoOpened(false);
    }
    setOpen(next);
  };

  if (!active) return null;

  const hasComment = comment.trim().length > 0;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={path ? `field-${path}` : undefined}
          aria-label={hasComment ? `Edit note for ${label}` : `Add a note for ${label}`}
          className="flex size-6 shrink-0 scroll-mt-28 items-center justify-center rounded-t-full rounded-br-full border-[0.6px] border-[#d4d4d4] bg-[#fafafa] transition-transform hover:scale-110"
        >
          {hasComment ? (
            <AuthorAvatar name={authorName} size="sm" />
          ) : (
            <ChatCircleText size={13} className="text-muted-foreground" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-76 space-y-3 rounded-2xl p-4 shadow-lg"
        align="start"
        sideOffset={8}
        // An auto-opened popover must never take focus: the edit that summoned
        // it may be mid-keystroke in a text field, and both grabbing focus on
        // open and handing it back to the marker on close would interrupt
        // typing. Clicking the marker still focuses the comment box normally.
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          if (!autoOpened) boxRef.current?.focus();
        }}
        onCloseAutoFocus={(e) => {
          if (autoOpened) e.preventDefault();
        }}
      >
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            {/* Same avatar the Comments sheet stamps on the posted note — you
                see who you're about to sign it as before you write it. */}
            <AuthorAvatar name={authorName} />
            <span className="text-body-md-medium text-foreground">Add a note?</span>
          </div>
          <div className="text-caption-regular text-muted-foreground">
            <span className="text-foreground">{label}</span> — you just changed this
          </div>
        </div>
        <Textarea
          ref={boxRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Write a comment"
          rows={3}
          className="field-sizing-fixed min-h-0 resize-none rounded-xl border-transparent bg-muted shadow-none focus-visible:border-transparent"
        />
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-body-sm-regular text-muted-foreground transition-colors hover:text-foreground"
          >
            Skip
          </button>
          <Button
            type="button"
            size="sm"
            onClick={() => {
              onCommentChange(draft);
              setOpen(false);
            }}
          >
            Post
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
